import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { GripIcon } from '@rsos/assets/icons';
import { default as SP } from '@rsos/react-split-pane';
import { StyledSplitPane, Resizer, PaneContainer } from './SplitPane.styles';
import {
  ADDRESS_PADDING,
  CARD_PADDING,
  CHAT_HEADER_HEIGHT,
  CHAT_MESSAGE_PADDING,
  DEFAULT_GRABBABLE_HEIGHT,
  DEFAULT_OFFSET,
  TOTAL_VERTICAL_PADDING,
} from './helpers';

const SplitPane = ({
  children,
  height,
  width = '100%',
  grabbableHeight = DEFAULT_GRABBABLE_HEIGHT, // grabbable height + bottom border
  disabled = false,
  offset = DEFAULT_OFFSET,
  paneStyle = {},
  onDragFinished = () => {},
  ...rest
}) => {
  const [isDragging, setIsDragging] = useState(false);

  // Note that this is the available height minus the grabbable resizer.
  const availableHeight = height - grabbableHeight - offset;

  // Design requirement: distribute panes 60:40 on initial load
  const bottomHeight = availableHeight * 0.4;

  const topPanePaddingOffset =
    TOTAL_VERTICAL_PADDING +
    ADDRESS_PADDING +
    TOTAL_VERTICAL_PADDING +
    CARD_PADDING;

  // Design requirement: min height of the bottom pane should be 20% of height
  const minSize =
    availableHeight * 0.2 - ADDRESS_PADDING + CHAT_MESSAGE_PADDING;

  // Design requirement: max height of the bottom pane should be 80% of height
  const maxSize =
    availableHeight * 0.8 - topPanePaddingOffset - grabbableHeight;

  if (disabled) {
    return <div style={{ ...paneStyle, height }}>{children}</div>;
  }

  return (
    <StyledSplitPane height={height} width={width}>
      <SP
        onDragStarted={() => {
          setIsDragging(true);
        }}
        onDragFinished={height => {
          setIsDragging(false);
          onDragFinished(height);
        }}
        split="horizontal"
        width={width}
        primary="second"
        defaultSize={bottomHeight}
        maxSize={maxSize}
        minSize={minSize}
        pane1Style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 'auto',
          // important - overflow must be set, so children in flex-grow: 1, can
          // span available space and show scrollbars if own content overflows.
          overflow: 'hidden',
        }}
        pane2Style={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: maxSize,
          minHeight: CHAT_HEADER_HEIGHT,
        }}
        customResizer={
          <Resizer>
            <GripIcon />
          </Resizer>
        }
        paneStyle={paneStyle}
        {...rest}
      >
        {/* TODO:
        `shortid.generate()` re-renders the child components and causes TalkJS not able to mount
        the chat iframe properly. so the short-term solution is to rely on indexes for keys.
        in the long run look into a better implementation of the TalkJS chat iframe */}
        {children.map((child, index) => (
          <PaneContainer isDragging={isDragging} key={index}>
            {child}
          </PaneContainer>
        ))}
      </SP>
    </StyledSplitPane>
  );
};

SplitPane.propTypes = {
  height: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  onDragFinished: PropTypes.func,
};

export default SplitPane;
